import urlParse from 'url-parse';
import axios from 'axios';
import { map } from 'lodash';
import { AsyncStorage } from 'services';
import { providers } from 'helpers/constants';
import { apiOrganizationEndpoints, apiIpEndpoint } from 'helpers/endpoints';
import { GDPRCountries } from 'helpers/gdpr';

import {
  restApiUrl,
  microsoftAuthCode,
  webAppRoot,
  isChromeExtension,
  appEnvironment,
} from 'helpers/constants';

const { origin } = window.location;

/**
 * Extracts the top level domain from an env variable
 * ex: 'http://localhost:3000' -> 'localhost:3000'
 * ex: 'https://usefyi.com -> 'usefyi.com'
 */
function extractTopLevelDomain() {
  const HOST_REGEX = /^https?:\/\/([^/?#]+)(?:[/?#]|$)/i;
  const APP_ROOT = webAppRoot || 'http://localhost:3000/';
  return APP_ROOT.match(HOST_REGEX)![1];
}

/**
 * Returns the auth_code for a provider
 * @param provider
 */
function getAuthCode(provider?: Provider) {
  return provider === providers.microsoft.id ? '&auth_code=' + microsoftAuthCode : '';
}

/**
 * Makes a request to check if the user is in
 * a country inside GDPR legislation
 */
export const needsGDPRConsent = async () => {
  return await axios
    .get(apiIpEndpoint.ipEndpoint, {
      headers: { 'Content-Type': 'application/json' },
    })
    .then((response) => {
      const countryCode = response.data.countryCode;
      return GDPRCountries.includes(countryCode);
    })
    .catch((error) => {
      return false;
    });
};

/**
 * Returns true if the app is running in a subdomain (mycompany.usefyi.com)
 * and false if otherwise (usefyi.com)
 */
export const isSubdomain = () => {
  if (isChromeExtension) {
    return false;
  }

  const topLevelDomain = extractTopLevelDomain(); // "localhost:3000"
  const currentWebsite = window.location.host; // without protocol - eg "localhost:3000" or "fyi.localhost:3000"
  const isOrganization = topLevelDomain !== currentWebsite;

  return isOrganization;
};

/**
 * Fetches the organisation name
 */
export const getOrganizationName = async () => {
  const topLevelDomain = extractTopLevelDomain(); // "localhost:3000"
  const currentWebsite = window.location.host; // without protocol - eg "localhost:3000" or "fyi.localhost:3000"
  const subdomain = currentWebsite.substring(0, currentWebsite.indexOf('.' + topLevelDomain));

  return await axios
    .get(apiOrganizationEndpoints(subdomain).organizationDomain, {
      headers: { 'Content-Type': 'application/json' },
    })
    .then((response) => {
      return response.data.name.toUpperCase();
    })
    .catch((error) => {
      return 'OrganizationNotFound';
    });
};

/**
 * Generates the oAuth URL
 * @param param
 */
export const generateOAuthURL = ({ provider }: { provider?: Provider } = {}) => {
  const authCode = getAuthCode(provider);
  let OAUTH_REDIRECT_URL = '';

  switch (appEnvironment) {
    case 'chrome':
      OAUTH_REDIRECT_URL = `${origin}/index.html?${authCode}#login?action=signup`;
      break;

    case 'web':
      OAUTH_REDIRECT_URL = `${webAppRoot}login?action=signup`;
      break;

    default:
      break;
  }

  const completeUri = urlParse(OAUTH_REDIRECT_URL, true) as any;
  const oauthURL = `${provider}/${'login'}`;

  const output = [
    restApiUrl,
    'oauth/start?oauth_url=',
    encodeURIComponent(oauthURL),
    authCode,
    '&complete_uri=',
    encodeURIComponent(completeUri),
  ].join('');

  return output;
};

/**
 * Sets the window location to the url param
 * @param url
 */
export const openAuthURL = (
  url: string,
  openNewWindow = false,
  windowName = '_blank',
  onClose?: () => void,
  onError?: () => void
) => {
  if (openNewWindow) {
    const windowSettings = {
      width: 600,
      height: 800,
      menubar: 0,
      toolbar: 0,
      location: 0,
      personalbar: 0,
      status: 0,
      dependent: 1,
    };

    const options = map(windowSettings, (value: string, key: string) => `${key}=${value}`).join(
      ','
    );

    const openWindow = window.open(url, windowName, options);

    if (openWindow === null) {
      onError && onError();
    }

    window.onmessage = (event: any) => {
      const { message } = event.data;
      if (message && message === 'access_denied' && openWindow) {
        openWindow.close();
      }
    };

    if (onClose && typeof onClose === 'function' && openWindow) {
      const checkOpenWindowTimer = setInterval(() => {
        if (openWindow.closed) {
          clearInterval(checkOpenWindowTimer);
          onClose();
        }
      }, 500);
    }
  } else {
    (window.location as any) = url;
  }
};

export const saveTokens = async (accessToken: string, refreshToken: string) => {
  await AsyncStorage.set('accessToken', accessToken);
  await AsyncStorage.set('refreshToken', refreshToken);
  return;
};

export const migrateTokens = async () => {
  const oldRefreshToken = localStorage.getItem('refreshToken');
  if (oldRefreshToken) {
    await AsyncStorage.set('refreshToken', oldRefreshToken as string);
  }
  const oldAccessToken = localStorage.getItem('accessToken');
  if (oldAccessToken) {
    await AsyncStorage.set('accessToken', oldAccessToken as string);
  }
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.location.reload();
  return;
};

export const saveGroupLoginGroupId = async (groupLoginGroupId: string) => {
  await AsyncStorage.set('groupLoginGroupId', groupLoginGroupId);
  return;
};

export const removeGroupLoginGroupId = async () => {
  AsyncStorage.remove('groupLoginGroupId');
  return;
};
